/**
 * Feedback — die Meldungen aller Nutzer für den Plattform-Admin (HM-169,
 * ADR 0047). Liste unter `/admin/feedback?art=&status=`, eine Meldung unter
 * `/admin/feedback/{id}`.
 *
 * Aufbau nach `ui_kits/dashboard/Feedback senden.html`, Abschnitte 4 und 5.
 * **Übernommen ist der Aufbau, nicht der Ort**: Der Entwurf zeichnet eine eigene
 * „Plattform-Verwaltung" mit eigener Seitenleiste. Hier hängt alles, was der
 * Installation gehört, am Kontomenü — „System", „Anlagen und Nutzer",
 * „Nutzung" —, und eine zweite Leiste nur für einen Eintrag wäre ein zweiter
 * Rahmen.
 *
 * **Filter und geöffnete Meldung stehen in der Adresse**, wie jede Einstellung
 * einer Ansicht in dieser App; „zurück" führt in dieselbe Auswahl.
 *
 * **Entschieden wird mit Knöpfen, nicht mit einer freien Statuswahl** (#76,
 * ADR 0054). Welche gerade gehen, sagt der Server in `decisions`; sobald ein
 * Issue anhängt, ist es keine mehr, und die Karte sagt, dass Gitea führt.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, EmptyState, SegmentedControl, Textarea, icons, useNarrow, breakpoints } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import {
  FEEDBACK_KIND_LABELS,
  FEEDBACK_STATUS_LABELS,
  FEEDBACK_STATUS_TONES,
  feedbackDecisionLabel,
  feedbackFiltered,
  feedbackSiteText,
  feedbackSyncText,
  type FeedbackDecision,
} from "../lib/feedback.js";
import { formatDateTime } from "../lib/format.js";
import { useAdminFeedback, useAdminFeedbackItem } from "../lib/queries.js";
import {
  FEEDBACK_STATUS_FILTERS,
  navigate,
  routePath,
  type FeedbackKindFilter,
  type Route,
} from "../lib/router.js";

type FeedbackRoute = Extract<Route, { kind: "feedback" }>;
type Item = ApiComponents["schemas"]["AdminFeedback"];

const sectionHead: CSSProperties = {
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--text-faint)",
  marginBottom: 8,
};

export function FeedbackScreen({ route }: { readonly route: FeedbackRoute }) {
  return route.feedbackId === null ? <FeedbackList route={route} /> : <FeedbackDetail route={route} feedbackId={route.feedbackId} />;
}

function FeedbackList({ route }: { readonly route: FeedbackRoute }) {
  const narrow = useNarrow(breakpoints.shell);
  const list = useAdminFeedback(route.feedbackKind, route.status);
  const set = (change: Partial<Pick<FeedbackRoute, "feedbackKind" | "status">>): void => navigate({ ...route, ...change });
  const counts = list.data?.counts;
  const total = counts === undefined ? undefined : counts.open + counts.accepted + counts.done + counts.rejected;
  const columns = "88px minmax(0,1fr) minmax(120px,190px) 190px 160px";

  return (
    <Card
      title="Feedback"
      subtitle={
        list.data === undefined
          ? "Fehlermeldungen und Wünsche aus Web-App und Apps"
          : `${list.data.openTotal} offen · Der Absender wird über Änderungen nicht benachrichtigt.`
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
          <SegmentedControl
            label="Art"
            size="sm"
            value={route.feedbackKind ?? "all"}
            onChange={(value) => set({ feedbackKind: value === "all" ? null : value })}
            options={[
              { value: "all", label: "Alle" },
              { value: "bug", label: "Fehler" },
              { value: "wish", label: "Wünsche" },
            ]}
          />
          <div role="group" aria-label="Status" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <Chip on={route.status === null} count={total} onClick={() => set({ status: null })}>
              Alle
            </Chip>
            {FEEDBACK_STATUS_FILTERS.map((status) => (
              <Chip key={status} on={route.status === status} count={counts?.[status]} onClick={() => set({ status })}>
                {FEEDBACK_STATUS_LABELS[status]}
              </Chip>
            ))}
          </div>
        </div>

        {list.isError && <Alert>{list.error.message}</Alert>}

        {list.data !== undefined && list.data.items.length === 0 ? (
          feedbackFiltered(route.feedbackKind, route.status) ? (
            <p style={{ margin: 0, padding: "18px 0", textAlign: "center", fontSize: 13.5, color: "var(--text-muted)" }}>
              Keine Meldungen mit dieser Auswahl.
            </p>
          ) : (
            <EmptyState
              icon={<icons.IconMessage size={22} />}
              title="Noch kein Feedback"
            >
              Meldungen erscheinen hier, sobald jemand im Kontomenü oder unter „Mehr“ in der App „Feedback senden“ benutzt.
            </EmptyState>
          )
        ) : (
          <div style={{ border: "1px solid var(--border-subtle)", borderRadius: "var(--radius-lg)", overflow: "hidden" }}>
            {!narrow && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: columns,
                  gap: 16,
                  padding: "10px 18px",
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--text-faint)",
                  borderBottom: "1px solid var(--border-subtle)",
                  background: "var(--surface-inset)",
                }}
              >
                <span>Art</span>
                <span>Titel</span>
                <span>Absender</span>
                <span>Eingegangen</span>
                <span>Status</span>
              </div>
            )}
            {(list.data?.items ?? []).map((item, index) => {
              const target: FeedbackRoute = { ...route, feedbackId: item.id };
              return (
                <a
                  key={item.id}
                  href={routePath(target)}
                  onClick={(event) => follow(event, target)}
                  style={{
                    display: narrow ? "flex" : "grid",
                    flexDirection: "column",
                    gridTemplateColumns: columns,
                    gap: narrow ? 6 : 16,
                    alignItems: narrow ? "stretch" : "center",
                    padding: narrow ? "12px 14px" : "12px 18px",
                    borderTop: index > 0 ? "1px solid var(--border-subtle)" : "none",
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  {narrow ? (
                    <>
                      <span style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <KindLabel kind={item.kind} />
                        <StatusMark item={item} />
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-strong)", lineHeight: 1.35 }}>{item.title}</span>
                      <span style={{ fontSize: 12, color: "var(--text-muted)", overflowWrap: "anywhere" }}>
                        {item.from} · <span style={{ fontFamily: "var(--font-mono)" }}>{formatDateTime(item.createdAt)}</span>
                      </span>
                    </>
                  ) : (
                    <>
                      <KindLabel kind={item.kind} />
                      <span
                        style={{
                          fontSize: 13.5,
                          fontWeight: 600,
                          lineHeight: 1.4,
                          color: "var(--text-strong)",
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {item.title}
                      </span>
                      <span style={{ fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.from}</span>
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>
                        {formatDateTime(item.createdAt)}
                      </span>
                      <StatusMark item={item} />
                    </>
                  )}
                </a>
              );
            })}
          </div>
        )}
        {list.data?.truncated === true && (
          <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)" }}>
            Gezeigt werden die neuesten 500. Ein Filter grenzt weiter ein.
          </p>
        )}
      </div>
    </Card>
  );
}

function FeedbackDetail({ route, feedbackId }: { readonly route: FeedbackRoute; readonly feedbackId: string }) {
  const narrow = useNarrow(breakpoints.shell);
  const item = useAdminFeedbackItem(feedbackId);
  const back: FeedbackRoute = { ...route, feedbackId: null };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <a
        href={routePath(back)}
        onClick={(event) => follow(event, back)}
        style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, fontWeight: 600, color: "var(--hm-green-700)", textDecoration: "none" }}
      >
        <icons.IconArrowLeft size={16} />
        Feedback
      </a>

      {item.isError && <Alert>{item.error.message}</Alert>}
      {item.data !== undefined && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <KindLabel kind={item.data.kind} />
              <StatusMark item={item.data} linked />
            </span>
            <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: narrow ? 19 : 22, fontWeight: 700, lineHeight: 1.3, color: "var(--text-strong)" }}>
              {item.data.title}
            </h2>
            <div style={{ fontSize: 13, color: "var(--text-muted)", overflowWrap: "anywhere" }}>
              {item.data.from} · <span style={{ fontFamily: "var(--font-mono)" }}>{formatDateTime(item.data.createdAt)}</span>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: narrow ? "minmax(0,1fr)" : "minmax(0,1fr) 300px", gap: 16, alignItems: "start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Card>
                <div style={sectionHead}>Beschreibung</div>
                <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{item.data.body}</div>
              </Card>
              <Card>
                <div style={sectionHead}>Mitgesendet</div>
                <Row label="App-Fassung" value={item.data.app} mono />
                <Row label="Browser / Gerät" value={item.data.client} />
                <Row label="Seite" value={item.data.page} />
                <Row label="Anlage" value={feedbackSiteText(item.data.site)} faint={item.data.site.site === null} />
              </Card>
            </div>
            {/* Ein Schlüssel je Meldung: Eine angefangene Notiz gehört zu der Meldung, an der
                sie begonnen wurde. */}
            <Decision key={item.data.id} item={item.data} />
          </div>
        </>
      )}
    </div>
  );
}

function Decision({ item }: { readonly item: Item }) {
  const queryClient = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const syncText = feedbackSyncText(item);

  const decide = useMutation({
    mutationFn: async (decision: FeedbackDecision) => {
      const trimmed = note.trim();
      const { data, error } = await api.POST("/admin/feedback/{feedbackId}/decision", {
        params: { path: { feedbackId: item.id } },
        body: { decision, ...(decision === "reject" && trimmed.length > 0 ? { note: trimmed } : {}) },
      });
      if (error || !data) throw new Error(apiErrorMessage(error) ?? "Das ging nicht.");
      return data;
    },
    onSuccess: (updated) => {
      setRejecting(false);
      setNote("");
      queryClient.setQueryData(["admin-feedback-item", updated.id], updated);
      void queryClient.invalidateQueries({ queryKey: ["admin-feedback"] });
    },
  });

  return (
    <Card>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <div style={sectionHead}>Entscheidung</div>
          {syncText !== null && <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>{syncText}</p>}
          {item.issue !== null && <IssueLink issue={item.issue} />}
          {item.status === "done" && item.statusChangedAt !== null && (
            <p style={{ margin: "6px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>
              seit <span style={{ fontFamily: "var(--font-mono)" }}>{formatDateTime(item.statusChangedAt)}</span>
            </p>
          )}
          {item.status === "open" && <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>Noch nicht entschieden. Angenommen wird sie als Issue in Gitea übernommen.</p>}
          {item.status === "rejected" && (
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>
              Abgelehnt{item.statusChangedAt === null ? "" : ` am ${formatDateTime(item.statusChangedAt)}`}.
            </p>
          )}
          {item.note !== null && (
            <p style={{ margin: "8px 0 0", padding: "8px 10px", borderRadius: "var(--radius-sm)", background: "var(--surface-inset)", fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {item.note}
            </p>
          )}
          {item.trackerRef !== null && item.issue === null && (
            <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>
              Kennung von früher: <span style={{ fontFamily: "var(--font-mono)" }}>{item.trackerRef}</span> — wird nicht noch einmal übernommen.
            </p>
          )}
        </div>

        {rejecting ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Textarea
              label="Notiz (optional)"
              hint="Nur für dich, der Absender sieht sie nicht."
              rows={3}
              maxLength={1000}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <Button variant="danger" disabled={decide.isPending} onClick={() => decide.mutate("reject")}>
                Ablehnen
              </Button>
              <Button variant="ghost" disabled={decide.isPending} onClick={() => setRejecting(false)}>
                Abbrechen
              </Button>
            </div>
          </div>
        ) : (
          item.decisions.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {item.decisions.map((decision) => (
                <Button
                  key={decision}
                  variant={decision === "accept" ? "primary" : "secondary"}
                  disabled={decide.isPending}
                  onClick={() => (decision === "reject" ? setRejecting(true) : decide.mutate(decision))}
                >
                  {feedbackDecisionLabel(decision, item.status)}
                </Button>
              ))}
            </div>
          )
        )}
        {decide.isError && <Alert>{decide.error.message}</Alert>}
        <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
          Der Absender wird über Änderungen nicht benachrichtigt. Ins Issue gehen weder seine Adresse noch die Anlage.
        </p>
      </div>
    </Card>
  );
}

/** Das Issue als Link, wenn der Server eine Adresse kennt; sonst die Nummer. */
function IssueLink({ issue }: { readonly issue: { readonly number: number; readonly url: string | null } }) {
  const label = `#${issue.number}`;
  return issue.url === null ? (
    <span style={{ fontFamily: "var(--font-mono)", fontSize: 12.5 }}>{label}</span>
  ) : (
    <a
      href={issue.url}
      target="_blank"
      rel="noreferrer"
      style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 6, fontSize: 13, fontWeight: 600, color: "var(--hm-green-700)" }}
    >
      {label} in Gitea öffnen
      <icons.IconArrowUpRight size={14} />
    </a>
  );
}

function KindLabel({ kind }: { readonly kind: FeedbackKindFilter }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap" }}>
      <span style={{ display: "inline-flex", color: "var(--text-muted)" }}>
        {kind === "bug" ? <icons.IconBug size={15} /> : <icons.IconBulb size={15} />}
      </span>
      {FEEDBACK_KIND_LABELS[kind]}
    </span>
  );
}

/**
 * Status und, wo es eins gibt, das Issue. In der Liste nur die Nummer — die
 * ganze Zeile ist dort schon ein Link, und ein zweiter darin wäre ein
 * verschachtelter `<a>`.
 */
function StatusMark({ item, linked = false }: { readonly item: Item; readonly linked?: boolean }) {
  const extra =
    item.issue !== null ? `#${item.issue.number}` : item.awaitsIssue ? "wartet auf Übernahme" : item.trackerRef;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
      <Badge tone={FEEDBACK_STATUS_TONES[item.status]} dot>
        {item.issue !== null && item.status === "accepted" ? "übernommen" : FEEDBACK_STATUS_LABELS[item.status]}
      </Badge>
      {extra !== null && !(linked && item.issue !== null) && (
        <span style={{ fontFamily: item.awaitsIssue ? "inherit" : "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>{extra}</span>
      )}
    </span>
  );
}

function Chip({ on, count, onClick, children }: { readonly on: boolean; readonly count: number | undefined; readonly onClick: () => void; readonly children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: 30,
        padding: "0 11px",
        borderRadius: 999,
        cursor: "pointer",
        fontFamily: "var(--font-body)",
        fontSize: 12.5,
        fontWeight: 600,
        border: `1px solid ${on ? "var(--hm-green-500)" : "var(--border-default)"}`,
        background: on ? "var(--hm-green-50)" : "var(--surface-card)",
        color: on ? "var(--hm-green-700)" : "var(--text-body)",
      }}
    >
      {children}
      {count !== undefined && (
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, fontWeight: 500, color: on ? "var(--hm-green-700)" : "var(--text-faint)" }}>{count}</span>
      )}
    </button>
  );
}

function Row({ label, value, mono = false, faint = false }: { readonly label: string; readonly value: string | null; readonly mono?: boolean; readonly faint?: boolean }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(96px,132px) minmax(0,1fr)", gap: 12, padding: "7px 0", fontSize: 13, borderTop: "1px solid var(--border-subtle)" }}>
      <span style={{ color: "var(--text-muted)" }}>{label}</span>
      <span
        style={{
          color: value === null || faint ? "var(--text-faint)" : "var(--text-strong)",
          fontFamily: mono ? "var(--font-mono)" : "inherit",
          fontSize: mono ? 12.5 : 13,
          overflowWrap: "anywhere",
        }}
      >
        {value ?? "nicht erkannt"}
      </span>
    </div>
  );
}

/** Ein Link, der in der Anwendung bleibt — Strg-, Cmd- und Mittelklick öffnen weiter einen neuen Tab. */
function follow(event: MouseEvent<HTMLAnchorElement>, target: Route): void {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  navigate(target);
}
